import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { useLangPrefix } from '../hooks/useLocalePath'
import SEO from '../components/SEO'
import api from '../api'
import '../styles/Auth.css'

export default function Register() {
  const { t } = useTranslation()
  const langPrefix = useLangPrefix()

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const inviteEmail = searchParams.get('email')
  const inviteProject = searchParams.get('project')
  const inviteToken = searchParams.get('invite')

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState(inviteEmail || '')
  const [password, setPassword] = useState('')
  const [acceptCGV, setAcceptCGV] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { register } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError(t('register.errorPasswordLength'))
      return
    }

    if (!acceptCGV) {
      setError(t('register.errorCGV'))
      return
    }

    setLoading(true)
    try {
      await register(firstName, lastName, email, password)

      // If invited to a project, auto-join as reviewer
      if (inviteProject) {
        try {
          await api.post(`/projects/${inviteProject}/join`)
        } catch (joinErr) {
          console.warn('Auto-join project failed:', joinErr)
        }
      }

      // If there's an invite token, try to accept it
      if (inviteToken) {
        try {
          await api.post(`/invitations/accept/${inviteToken}`)
        } catch (acceptErr) {
          console.warn('Auto-accept invitation failed:', acceptErr)
        }
      }

      navigate(inviteProject ? langPrefix + `/projects/${inviteProject}` : langPrefix + '/dashboard')
    } catch (err) {
      setError(err.response?.data?.message || t('register.errorDefault'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <SEO
        title={t('register.seo.title')}
        description={t('register.seo.description')}
        path="/register"
      />
      <div className="auth-card">
        <div className="auth-logo">
          <span className="auth-logo-gradient">ReviewSight</span>
        </div>

        <h1 className="auth-title">{inviteProject ? t('register.titleInvited') : t('register.titleDefault')}</h1>
        <p className="auth-subtitle">
          {inviteProject
            ? t('register.subtitleInvited')
            : t('register.subtitleDefault')}
        </p>

        {error && (
          <div className="auth-error" role="alert">
            <span className="auth-error-icon">⚠</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name" className="form-label">{t('register.firstNameLabel')}</label>
              <input
                id="first_name"
                type="text"
                className="form-input"
                placeholder={t('register.firstNamePlaceholder')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="last_name" className="form-label">{t('register.lastNameLabel')}</label>
              <input
                id="last_name"
                type="text"
                className="form-input"
                placeholder={t('register.lastNamePlaceholder')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">{t('register.emailLabel')}</label>
            <input
              id="email"
              type="email"
              className="form-input"
              placeholder={t('register.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              readOnly={!!inviteEmail}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">{t('register.passwordLabel')}</label>
            <input
              id="password"
              type="password"
              className="form-input"
              placeholder={t('register.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <div className="form-group form-group-cgv">
            <label className="cgv-label">
                            <input
                type="checkbox"
                checked={acceptCGV}
                onChange={(e) => setAcceptCGV(e.target.checked)}
                className="cgv-checkbox-hidden"
              />
              <span className={`cgv-check-box ${acceptCGV ? 'checked' : ''}`}>
                {acceptCGV && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              <span className="cgv-text">
                {t('register.cgvAccept')}{' '}
                <Link to={langPrefix + '/cgu'} target="_blank" className="auth-link">{t('register.cgvLink')}</Link>
                {' '}{t('register.cgvAnd')}{' '}
                <Link to={langPrefix + '/confidentialite'} target="_blank" className="auth-link">{t('register.privacyLink')}</Link>
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? (
              <span className="btn-loading">
                <span className="spinner" />
                {t('register.submitLoading')}
              </span>
            ) : (
              t('register.submit')
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('register.alreadyAccount')}{' '}
            <Link to={langPrefix + '/login'} className="auth-link">{t('register.loginLink')}</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
